import { CaseData, ExpenseTemplateModel, ExpenseGroupModel, ExpenseModel, ExpenseGroupStatus } from '../types/case';
import { newFinancialLineId } from './financialLineId';
import { CurrentUserResponse } from '../types/auth';
import type { CalcVersionAvailability } from '../types/calcVersion';
import { getDefaultTaxYear } from './calcVersionAvailability';

/** Shown when no state/year combination is enabled for new cases. */
export const NO_TAX_YEAR_AVAILABLE_MESSAGE = 'No tax year is available for new cases yet. Contact support.';

/**
 * Default "Calculist Standard" expense template used when no team/user default exists
 */
const CALCULIST_STANDARD_TEMPLATE: ExpenseTemplateModel = {
    name: 'Calculist Standard',
    groups: [
        {
            name: 'Housing',
            expenses: [
                { name: 'Mortgage (including escrow)' },
                { name: 'Utilities (including internet)' },
                { name: 'Maintenance (Yard/Snow Removal)' },
                { name: 'Cable (streaming service)' },
                { name: 'Cell Phone' },
            ],
        },
        {
            name: 'Vehicle',
            expenses: [
                { name: 'Monthly Installment Loan' },
                { name: 'Insurance' },
                { name: 'Vehicle Maintenance' },
                { name: 'Gas' },
                { name: 'Registration' },
            ],
        },
        {
            name: 'Personal',
            expenses: [
                { name: 'Food/Dining Out' },
                { name: 'Clothing' },
                { name: 'Incidentals' },
                { name: 'Entertainment/Travel' },
                { name: 'Gifts' },
                { name: 'Charitable Contributions' },
                { name: 'Membership Fees' },
                { name: 'Pet Expense' },
                { name: 'Daycare Expense' },
            ],
        },
        {
            name: 'Monthly Payments',
            expenses: [],
        },
        {
            name: 'Other',
            expenses: [],
        },
    ],
};

/**
 * Resolves the expense template to use as a case's starting point: the user's personal
 * default, falling back to the team's default, falling back to CALCULIST_STANDARD_TEMPLATE
 * when neither is set or the referenced template id isn't present in the team's list.
 * Shared by `createNewCase` (case creation) and BudgetPage's load-time seed effect (a case
 * whose budget was never populated — see docs/AUTOSAVE_DETAILS.md section 13) so both paths
 * resolve the same default template identically.
 */
export function resolveDefaultExpenseTemplate(
    currentUser: CurrentUserResponse | null | undefined
): ExpenseTemplateModel {
    let defaultTemplate: ExpenseTemplateModel | undefined;
    const defaultTemplateId = currentUser?.defaultExpenseTemplateId || currentUser?.team?.defaultExpenseTemplateId;

    if (defaultTemplateId && currentUser?.team?.expenseTemplates) {
        defaultTemplate = currentUser.team.expenseTemplates.find(
            template => template.id === defaultTemplateId
        );
    }

    return defaultTemplate ?? CALCULIST_STANDARD_TEMPLATE;
}

/**
 * Converts an expense template's groups into a fresh `ExpenseGroupModel[]` ready to seed a
 * case: every expense gets a newly-minted `lineId` and starts at $0; every group is `active`.
 * Deliberately does NOT stamp `groupLineId` — the load-time lineId backfill
 * (`hasAnyMissingFinancialLineId`/`normalizeCaseData`, see docs/AUTOSAVE_DETAILS.md section 13)
 * mints it on first load, exactly as it already does for newly-created cases today.
 */
export function templateToExpenseGroups(template: ExpenseTemplateModel): ExpenseGroupModel[] {
    return (template.groups || []).map((templateGroup) => {
        const expenses = (templateGroup.expenses || []).map((templateExpense) => ({
            name: templateExpense.name || '',
            amount: 0,
            lineId: newFinancialLineId(),
        }));

        return {
            name: templateGroup.name || '',
            expenses: expenses,
            status: 'active' as ExpenseGroupStatus,
        };
    });
}

/**
 * Creates a new case with default structure
 * @param currentUser - The current user from AuthContext
 * @param isMarried - Whether the case is for a married couple (default: false)
 * @param calcVersions - Enabled state/year combinations (SystemSettingsContext). When given, the
 *   tax year is getDefaultTaxYear's choice and the call throws if there is none; when omitted, the
 *   current calendar year.
 * @returns A new CaseData object with default values
 */
export function createNewCase(
    currentUser: CurrentUserResponse | null | undefined,
    isMarried: boolean = false,
    calcVersions?: CalcVersionAvailability[]
): { caseId: string; caseData: CaseData } {
    // Validate that user and team information is available
    if (!currentUser) {
        throw new Error('Cannot create case: User information is missing. Please ensure you are logged in.');
    }

    if (!currentUser.team?.teamId) {
        throw new Error('Cannot create case: User team information is missing. Please contact support if this issue persists.');
    }

    const stateCode = 'IA';
    const taxYear = calcVersions ? getDefaultTaxYear(stateCode, calcVersions) : new Date().getFullYear();
    if (taxYear === null) {
        throw new Error(NO_TAX_YEAR_AVAILABLE_MESSAGE);
    }

    // Generate a new case ID
    const newCaseId = crypto.randomUUID();

    // Create a new case with default structure
    const newCaseData: CaseData = {
        audit: {
            createdAt: new Date().toISOString(),
            createdBy: currentUser?.userId || '',
            updatedAt: new Date().toISOString(),
            updatedBy: currentUser?.userId || '',
        },
        basic: {
            affidavit: {
                type: "notarySignatureBlock"
            },
            attorneyListedOnDocuments: (() => {
                // If current user is an attorney, use them
                if (currentUser?.businessRole?.toLowerCase() === 'attorney') {
                    return {
                        id: currentUser.userId,
                        firstName: currentUser.firstName,
                        lastName: currentUser.lastName,
                        teamName: currentUser.team?.name
                    };
                }

                // If current user is not an attorney, check if there's exactly 1 active attorney on the team
                if (currentUser?.team?.users) {
                    const activeAttorneys = currentUser.team.users.filter(
                        (user) =>
                            user.businessRole?.toLowerCase() === 'attorney' &&
                            user.status?.toLowerCase() === 'active'
                    );

                    if (activeAttorneys.length === 1) {
                        const attorney = activeAttorneys[0];
                        return {
                            id: attorney.userId,
                            firstName: attorney.firstName,
                            lastName: attorney.lastName,
                            teamName: currentUser.team?.name
                        };
                    }
                }

                return undefined;
            })(),
            caseId: newCaseId,
            stateCode,
            taxYear,
            caseStatus: 'working',
            isLocked: false,
            minorChildrenInvolved: true,
            isMarried: isMarried,
        },
        caseType: 'case',
        children: {
            count: 0,
            ...(isMarried ? {} : { taxSchedule: 'fixed' }),
            physicalCare: 'na',
            residence: {
                parties: 'na',
                children: 'na'
            }
        },
        financial: {
            assets: [],
            liabilities: [],
            expenseGroups: [],
            includeEqualizationPaymentInDivisionWorksheet: true
        },
        templates: {
            expenses: undefined,
        },
        metadata: {
            schemaVersion: 1,
        },
        documents: {
            summary: undefined,
            worksheet1: undefined,
        },
        parties: {
            partyA: {
                personal: {},
                income: {},
                tax: isMarried ? {} : {
                    claimSchedule: {
                        all: {
                            status: 'headOfHousehold',
                        },
                        even: {},
                        odd: {},
                    }
                },
                deductions: {},
                medical: {},
            },
            partyB: {
                personal: {},
                income: {},
                tax: isMarried ? {} : {
                    claimSchedule: {
                        all: {
                            status: 'single',
                        },
                        even: {},
                        odd: {},
                    }
                },
                deductions: {},
                medical: {},
            },
        },
        representation: isMarried ? {} : {
            petitionerParty: 'partyA',
            representedParty: 'neither',
            userId: currentUser?.userId || '',
        },
        sharing: {
            linkTokens: [],
        },
        team: {
            id: currentUser.team.teamId, // Already validated above, so safe to use
            name: currentUser.team.name || '',
            teamType: currentUser.team.teamType,
        },
        user: {
            id: currentUser?.userId || '',
            name: currentUser?.fullName || '',
        },
        validation: {},
    };

    // Apply default expense template if available, otherwise use Calculist Standard
    const defaultTemplate = resolveDefaultExpenseTemplate(currentUser);

    if (defaultTemplate) {
        newCaseData.financial.expenseGroups = templateToExpenseGroups(defaultTemplate);
        // Only set expenseTemplateId if it's a valid GUID (not empty string or undefined)
        newCaseData.financial.expenseTemplateId = defaultTemplate.id && defaultTemplate.id !== ''
            ? defaultTemplate.id
            : undefined;
        newCaseData.financial.expenseTemplateName = defaultTemplate.name || 'Calculist Standard';
        if (newCaseData.templates) {
            // Only set id if it's a valid GUID (not empty string or undefined)
            // Backend expects Guid? (nullable GUID), so undefined/null is fine, but empty string is not
            const templateId = defaultTemplate.id && defaultTemplate.id !== ''
                ? defaultTemplate.id
                : undefined;

            newCaseData.templates.expenses = {
                name: defaultTemplate.name,
                groups: defaultTemplate.groups,
            };

            // Only add id property if we have a valid GUID
            if (templateId) {
                newCaseData.templates.expenses.id = templateId;
            }
        }
    }

    return { caseId: newCaseId, caseData: newCaseData };
}
